import { Static, Type } from '@sinclair/typebox';
import { AiTone, AiVoice } from './ai-assist';

// =====================================================================
// Contexte IA du professionnel — « voix de marque » + consignes durables.
//
// Stockage 1-1 avec `professional` (table professional_ai_context). Injecté
// dans le prompt système de Pulse conversationnel ET du copilote inline via
// `buildProContextFragment` côté backend (point anti-divergence). Voir
// docs/to_implement/pulse-inline-copilot-and-pro-context-plan.md.
// =====================================================================

const enumLiteralUnion = (e: Record<string, string>): ReturnType<typeof Type.Union> =>
  Type.Union(Object.values(e).map((v): ReturnType<typeof Type.Literal> => Type.Literal(v)));

/** Provenance d'une consigne : apprise par Pulse ou saisie manuellement par le pro. */
export enum ProAiGuidelineSource {
  PULSE = 'pulse',
  MANUAL = 'manual',
}

/**
 * Une consigne durable du contexte IA. Note discrète et traçable (≠ blob
 * réécrit) : provenance connue, supprimable une à une, compilée en bloc texte
 * injecté dans les prompts.
 */
export const ProAiGuidelineSchema = Type.Object({
  id: Type.String({ maxLength: 64 }),
  text: Type.String({ minLength: 1, maxLength: 500 }),
  source: enumLiteralUnion(ProAiGuidelineSource),
  created_at: Type.String({ format: 'date-time', meta: { source_key: 'created_at', with_time: true } }),
});

export type ProAiGuideline = Static<typeof ProAiGuidelineSchema>;

/** Réponse de lecture du contexte IA du pro courant. */
export const ProAiContextSchema = Type.Object({
  default_voice: Type.Optional(enumLiteralUnion(AiVoice)),
  default_tone: Type.Optional(enumLiteralUnion(AiTone)),
  guidelines: Type.Array(ProAiGuidelineSchema),
});

export type ProAiContext = Static<typeof ProAiContextSchema>;

/** Mise à jour des défauts de voix/ton du contexte IA. */
export const UpdateProAiContextRequestSchema = Type.Object({
  default_voice: Type.Optional(enumLiteralUnion(AiVoice)),
  default_tone: Type.Optional(enumLiteralUnion(AiTone)),
});

export type UpdateProAiContextRequest = Static<typeof UpdateProAiContextRequestSchema>;

/** Ajout manuel d'une consigne par le professionnel. */
export const AddProAiGuidelineRequestSchema = Type.Object({
  text: Type.String({ minLength: 1, maxLength: 500 }),
});

export type AddProAiGuidelineRequest = Static<typeof AddProAiGuidelineRequestSchema>;

/**
 * Plafond du nombre de consignes conservées par pro — garde-fou contre la
 * dérive de l'auto-enrichissement (décision plan #5). Au-delà, on évince les
 * plus anciennes consignes de source `pulse`.
 */
export const PRO_AI_GUIDELINES_MAX = 30;
